import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { catMemes } from '@/mocks/catMemes';
import MemeHero from './components/MemeHero';
import FeaturedMeme from './components/FeaturedMeme';
import MemeCategoryFilter from './components/MemeCategoryFilter';
import MemeCard from './components/MemeCard';
import MemeSubmitBanner from './components/MemeSubmitBanner';

const memeSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Cat Memes - Weekly Fresh Funny Cat Memes | MeowCentral',
  url: `${import.meta.env.VITE_SITE_URL || 'https://meowcentral.com'}/memes`,
  description: 'The internet\'s freshest and funniest cat memes, updated every week. Share your favorites on Twitter, Facebook, Reddit and WhatsApp.',
};

export default function MemesPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'top'>('hot');

  const featuredMeme = useMemo(() => catMemes.find((m) => m.isFeatured), []);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: catMemes.length };
    catMemes.forEach((m) => {
      counts[m.category] = (counts[m.category] || 0) + 1;
    });
    return counts;
  }, []);

  const filteredMemes = useMemo(() => {
    let list = selectedCategory === 'all'
      ? catMemes
      : catMemes.filter((m) => m.category === selectedCategory);

    if (sortBy === 'hot') list = [...list].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0) || b.likes - a.likes);
    if (sortBy === 'top') list = [...list].sort((a, b) => b.likes - a.likes);
    if (sortBy === 'new') list = [...list].sort((a, b) => a.id - b.id);

    return list;
  }, [selectedCategory, sortBy]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      <SEOHead
        title="Cat Memes - Weekly Fresh Funny Cat Memes | MeowCentral"
        description="Browse the internet's freshest and funniest cat memes, updated every single week. Share your favorites on Twitter, Facebook, Reddit and WhatsApp instantly."
        keywords="cat memes, funny cat memes, cat humor, cat jokes, weekly cat memes, viral cat memes"
        canonical="/memes"
        schema={memeSchema}
      />
      <Navbar />

      <MemeHero />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Featured Meme of the Week */}
        {featuredMeme && <FeaturedMeme meme={featuredMeme} />}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex-1 overflow-x-auto">
            <MemeCategoryFilter
              selected={selectedCategory}
              onChange={setSelectedCategory}
              counts={categoryCounts}
            />
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 shadow-sm flex-shrink-0">
            {(['hot', 'top', 'new'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                  sortBy === s
                    ? 'bg-orange-500 text-white shadow'
                    : 'text-gray-500 hover:text-orange-500'
                }`}
              >
                {s === 'hot' && <><i className="ri-fire-line mr-1"></i>Hot</>}
                {s === 'top' && <><i className="ri-bar-chart-line mr-1"></i>Top</>}
                {s === 'new' && <><i className="ri-time-line mr-1"></i>New</>}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-400 font-medium mb-6">
          Showing <strong className="text-gray-700">{filteredMemes.length}</strong> memes
          {selectedCategory !== 'all' && <> in <strong className="text-orange-500">{selectedCategory}</strong></>}
        </p>

        {/* Meme Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMemes.map((meme, i) => (
            <MemeCard key={meme.id} meme={meme} index={i} />
          ))}
        </div>

        {/* Submit Banner */}
        <MemeSubmitBanner />

        {/* Weekly refresh notice */}
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-500 text-sm px-5 py-2.5 rounded-full shadow-sm">
            <i className="ri-refresh-line text-teal-500"></i>
            New memes drop every <strong className="text-gray-700">Monday</strong> — bookmark this page!
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
